"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfiguratorState = void 0;
const plattar_api_1 = require("@plattar/plattar-api");
class ConfiguratorState {
    constructor(state = null) {
        const defaultState = {
            meta: {
                scene_product_index: 0,
                product_variation_index: 1,
                meta_index: 2,
            },
            states: []
        };
        if (state) {
            try {
                const decodedb64State = atob(state);
                const parsedState = JSON.parse(decodedb64State);
                // set the meta data
                if (parsedState.meta) {
                    defaultState.meta.scene_product_index = parsedState.meta.scene_product_index || 0;
                    defaultState.meta.product_variation_index = parsedState.meta.product_variation_index || 1;
                    defaultState.meta.meta_index = parsedState.meta.meta_index || 2;
                }
                defaultState.states = parsedState.states || [];
            }
            catch (err) {
                console.error("ConfiguratorState.constructor() - there was an error parsing configurator state");
                console.error(err);
            }
        }
        this._state = defaultState;
    }
    /**
     * Adds a new Scene Product/Variation combo with meta-data into the Configurator State
     *
     * @param sceneProductID - The Scene Product ID to be used (as defined in Plattar CMS)
     * @param productVariationID - The Product Variation ID to be used (as defined in Plattar CMS)
     * @param metaData - Arbitrary meta-data that can be used against certain operaions
     */
    setSceneProduct(sceneProductID, productVariationID, metaData = null) {
        this.addSceneProduct(sceneProductID, productVariationID, metaData);
    }
    /**
     * Adds a new Scene Product/Variation combo with meta-data into the Configurator State
     *
     * @param sceneProductID - The Scene Product ID to be used (as defined in Plattar CMS)
     * @param productVariationID - The Product Variation ID to be used (as defined in Plattar CMS)
     * @param metaData - Arbitrary meta-data that can be used against certain operaions
     */
    addSceneProduct(sceneProductID, productVariationID, metaData = null) {
        if (sceneProductID && productVariationID) {
            const states = this._state.states;
            const meta = this._state.meta;
            let newData = null;
            const existingData = this.findSceneProductIndex(sceneProductID);
            if (existingData) {
                newData = existingData;
            }
            else {
                newData = [];
                // push the new data into the stack
                states.push(newData);
            }
            newData[meta.scene_product_index] = sceneProductID;
            newData[meta.product_variation_index] = productVariationID;
            if (metaData) {
                newData[meta.meta_index] = metaData;
            }
        }
    }
    /**
     * Search and return the data index reference for the provided Scene Product ID
     * if not found, will return null
     * @param sceneProductID
     */
    findSceneProductIndex(sceneProductID) {
        const states = this._state.states;
        if (states.length > 0) {
            const meta = this._state.meta;
            const found = states.find((productState) => {
                return productState[meta.scene_product_index] === sceneProductID;
            });
            return found ? found : null;
        }
        return null;
    }
    /**
     * Search and return the data for the provided Scene Product ID
     * if not found, will return null
     * @param sceneProductID
     */
    findSceneProduct(sceneProductID) {
        const found = this.findSceneProductIndex(sceneProductID);
        if (found) {
            const meta = this._state.meta;
            const data = {
                scene_product_id: found[meta.scene_product_index],
                product_variation_id: found[meta.product_variation_index],
                meta_data: {
                    augment: true
                }
            };
            // include the meta-data
            if (found.length === 3) {
                data.meta_data.augment = found[meta.meta_index].augment || true;
            }
            return data;
        }
        return null;
    }
    /**
     * Iterate over the internal state data
     */
    forEach(callback) {
        const states = this._state.states;
        const meta = this._state.meta;
        if (states.length > 0) {
            states.forEach((productState) => {
                if (productState.length === 2) {
                    callback({
                        scene_product_id: productState[meta.scene_product_index],
                        product_variation_id: productState[meta.product_variation_index],
                        meta_data: {
                            augment: true
                        }
                    });
                }
                else if (productState.length === 3) {
                    callback({
                        scene_product_id: productState[meta.scene_product_index],
                        product_variation_id: productState[meta.product_variation_index],
                        meta_data: {
                            augment: productState[meta.meta_index].augment || true
                        }
                    });
                }
            });
        }
    }
    /**
     * @returns Returns the first reference of data in the stack, otherwise returns null
     */
    first() {
        const states = this._state.states;
        if (states.length > 0) {
            const meta = this._state.meta;
            const found = states.find((productState) => {
                const check = productState[meta.scene_product_index];
                // ensure the data contains valid elements
                return check !== null && check !== undefined;
            });
            if (!found) {
                return null;
            }
            const data = {
                scene_product_id: found[meta.scene_product_index],
                product_variation_id: found[meta.product_variation_index],
                meta_data: {
                    augment: true
                }
            };
            // include the meta-data
            if (found.length === 3) {
                data.meta_data.augment = found[meta.meta_index].augment || true;
            }
            return data;
        }
        return null;
    }
    get length() {
        return this._state.states.length;
    }
    /**
     * Decodes and returns an instance of ConfiguratorState from a previously
     * encoded state
     * @param state - The previously encoded state as a Base64 String
     * @returns - ConfiguratorState instance
     */
    static decode(state) {
        return new ConfiguratorState(state);
    }
    /**
     * Generates a new ConfiguratorState instance from all SceneProducts and default
     * variations from the provided Scene ID
     * @param sceneID - the Scene ID to generate
     * @returns - Promise that resolves into a ConfiguratorState instance
     */
    static decodeScene(sceneID = null) {
        return new Promise((accept, reject) => {
            const configState = new ConfiguratorState();
            if (!sceneID) {
                return reject(new Error("ConfiguratorState.decodeScene(sceneID) - sceneID must be defined"));
            }
            const scene = new plattar_api_1.Scene(sceneID);
            scene.include(plattar_api_1.SceneProduct);
            scene.include(plattar_api_1.SceneProduct.include(plattar_api_1.Product));
            scene.get().then((scene) => {
                const sceneProducts = scene.relationships.filter(plattar_api_1.SceneProduct);
                // nothing to do if no AR components can be found
                if (sceneProducts.length <= 0) {
                    return accept(configState);
                }
                // add out scene models
                sceneProducts.forEach((sceneProduct) => {
                    const product = sceneProduct.relationships.find(plattar_api_1.Product);
                    if (product && product.attributes.product_variation_id) {
                        configState.setSceneProduct(sceneProduct.id, product.attributes.product_variation_id);
                    }
                });
                accept(configState);
            }).catch(reject);
        });
    }
    /**
     * Encode and return the internal ConfiguratorState as a Base64 String
     * @returns - Base64 String
     */
    encode() {
        return btoa(JSON.stringify(this._state));
    }
}
exports.ConfiguratorState = ConfiguratorState;
